import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface MemoryBook {
  id: string;
  title: string;
  physical_comments: number;
  unfinished_comments: number;
  online_comments: number;
  unfinished_online_comments: number;
  total_comments: number;
  status: "not_started" | "in_progress" | "completed";
  thumbnail?: string;
  assignmentId?: number;
  assignedAt?: string;
  dueDate?: string;
  inDanger?: boolean;
  role?: "scanner" | "uploader" | "both" | "other" | null;
}

interface MemoryBookState {
  selectedBook: MemoryBook | null;
  dailyGoal: number;
  actualScanned: number;
  actualOnlineUploaded: number;
  showLoginModal: boolean;
}

const initialState: MemoryBookState = {
  selectedBook: null,
  dailyGoal: 0,
  actualScanned: 0,
  actualOnlineUploaded: 0,
  showLoginModal: false,
};

const memoryBookSlice = createSlice({
  name: "memoryBook",
  initialState,
  reducers: {
    setSelectedBook: (state, action: PayloadAction<MemoryBook | null>) => {
      state.selectedBook = action.payload;
    },
    setDailyGoal: (state, action: PayloadAction<number>) => {
      state.dailyGoal = action.payload;
    },
    setActualScanned: (state, action: PayloadAction<number>) => {
      state.actualScanned = action.payload;
    },
    setActualOnlineScanned: (state, action: PayloadAction<number>) => {
      state.actualOnlineUploaded = action.payload;
    },
    setShowLoginModal: (state, action: PayloadAction<boolean>) => {
      state.showLoginModal = action.payload;
    },
    resetMemoryBookState: (state) => {
      state.selectedBook = null;
      state.dailyGoal = 0;
      state.actualScanned = 0;
      state.actualOnlineUploaded = 0;
      state.showLoginModal = false;
    },
    updateScannedComments: (
      state,
      action: PayloadAction<{ scanned: number }>
    ) => {
      if (!state.selectedBook) return;
      const newUnfinished = Math.max(
        state.selectedBook.unfinished_comments - action.payload.scanned,
        0
      );
      state.selectedBook = {
        ...state.selectedBook,
        unfinished_comments: newUnfinished,
        status:
          newUnfinished === 0 &&
          state.selectedBook.unfinished_online_comments === 0
            ? "completed"
            : "in_progress",
      };
      state.actualScanned += action.payload.scanned;
    },
    updateOnlineUploadedComments: (
      state,
      action: PayloadAction<{ uploaded: number }>
    ) => {
      if (!state.selectedBook) return;
      const newUnfinishedOnline = Math.max(
        state.selectedBook.unfinished_online_comments - action.payload.uploaded,
        0
      );
      state.selectedBook = {
        ...state.selectedBook,
        unfinished_online_comments: newUnfinishedOnline,
        status:
          newUnfinishedOnline === 0 &&
          state.selectedBook.unfinished_comments === 0
            ? "completed"
            : "in_progress",
      };
      state.actualOnlineUploaded += action.payload.uploaded;
    },
  },
});

export const {
  setSelectedBook,
  setDailyGoal,
  setActualScanned,
  setActualOnlineScanned,
  setShowLoginModal,
  resetMemoryBookState,
  updateScannedComments,
  updateOnlineUploadedComments,
} = memoryBookSlice.actions;

export default memoryBookSlice.reducer;